/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, September 2020
 */

import React from 'react';
import { StyledComponent } from '@emotion/styled';
import { Box, Label, styled, Theme } from '@staffino/primitive-controls';

type ChoiceBoxProps = {
  separator?: boolean;
  theme?: Theme;
};

const ChoiceBox: StyledComponent<
  ChoiceBoxProps & React.ComponentProps<typeof Box>
> = styled(Box)<ChoiceBoxProps>((props) => ({
  borderBottomColor: props.theme.colors.separator,
  borderBottomStyle: props.separator ? 'solid' : 'none',
  borderBottomWidth: 1,
  width: '100%',
  maxWidth: '30rem',
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: '1rem',
  paddingBottom: '1rem',
  paddingLeft: 0,
  paddingRight: 0,
  position: 'relative',
}));

type ChoiceWrapperProps = React.ComponentProps<typeof ChoiceBox> & {
  children?: React.ReactNode | React.ReactNode[];
};

export function ChoiceWrapper({
  color,
  separator,
  theme,
  children,
  ...rest
}: ChoiceWrapperProps) {
  return (
    <ChoiceBox separator={separator} theme={theme} {...rest}>
      <Label color={color}>{children}</Label>
    </ChoiceBox>
  );
}

type HighlightableChoiceBoxProps = {
  highlight?: boolean;
};

const HighlightableChoiceBox: StyledComponent<
  HighlightableChoiceBoxProps & React.ComponentProps<typeof ChoiceBox>
> = styled(ChoiceBox)<HighlightableChoiceBoxProps>((props) => ({
  '&:focus-within, &.active': {
    '&:after': {
      content: "''",
      position: 'absolute',
      bottom: 0,
      height: 1,
      background: props.highlight
        ? props.theme.colors.primary
        : props.theme.colors.separator,
      right: 0,
      left: '2.375rem',
    },
  },
}));

type HighlightableChoiceWrapperProps = React.ComponentProps<
  typeof HighlightableChoiceBox
> & { children?: React.ReactNode | React.ReactNode[] };

export function HighlightableChoiceWrapper({
  color,
  highlight,
  children,
  ...rest
}: HighlightableChoiceWrapperProps) {
  return (
    <HighlightableChoiceBox highlight={highlight} {...rest}>
      <Label color={color} display="flex" flex={1}>
        {children}
      </Label>
    </HighlightableChoiceBox>
  );
}
